import { useState } from "react";
import { Calendar, Globe, Loader2, Clock, Shield, AlertTriangle, CheckCircle, Building, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DomainInfo {
  domain: string;
  registrationDate: string | null;
  expirationDate: string | null;
  updatedDate: string | null;
  registrar: string | null;
  status: string[];
  ageInDays: number | null;
  ageFormatted: string | null;
}

export const DomainAgeChecker = () => {
  const [domain, setDomain] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<DomainInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sampleDomains = ["google.com", "github.com", "stripe.com", "notion.so", "vercel.com"];

  const loadSample = () => {
    const sample = sampleDomains[Math.floor(Math.random() * sampleDomains.length)];
    setDomain(sample);
    setResult(null);
    setError(null);
  };

  const checkDomain = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain to check");
      return;
    }

    setIsChecking(true);
    setResult(null);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("check-domain-age", {
        body: { domain: domain.trim() },
      });

      if (fnError) throw fnError;

      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
      }
    } catch (err) {
      console.error("Error checking domain:", err);
      setError("Failed to lookup domain. Please try again.");
    } finally {
      setIsChecking(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Unknown";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getAgeCategory = (days: number | null) => {
    if (!days) return { label: "Unknown", color: "text-muted-foreground", bg: "bg-muted" };
    if (days >= 1825) return { label: "Established", color: "text-success", bg: "bg-success/10" }; // 5+ years
    if (days >= 730) return { label: "Mature", color: "text-emerald-500", bg: "bg-emerald-500/10" }; // 2+ years
    if (days >= 365) return { label: "Growing", color: "text-primary", bg: "bg-primary/10" }; // 1+ year
    if (days >= 180) return { label: "Young", color: "text-warning", bg: "bg-warning/10" }; // 6+ months
    return { label: "New", color: "text-destructive", bg: "bg-destructive/10" };
  };

  const getDaysUntilExpiry = (expirationDate: string | null) => {
    if (!expirationDate) return null;
    const expiry = new Date(expirationDate);
    const now = new Date();
    return Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Domain Age Checker
          </h3>
          <p className="text-sm text-muted-foreground">
            Look up domain registration date and WHOIS information
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              Domain Name
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSample}
              className="text-xs text-primary hover:text-primary/80"
            >
              Load Sample
            </Button>
          </div>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={domain}
              onChange={(e) => {
                setDomain(e.target.value);
                setResult(null);
                setError(null);
              }}
              placeholder="example.com"
              className="pl-10"
              onKeyDown={(e) => e.key === "Enter" && checkDomain()}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Enter a domain without http:// or www
          </p>
        </div>

        <Button
          onClick={checkDomain}
          disabled={isChecking || !domain.trim()}
          className="w-full"
        >
          {isChecking ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Looking up WHOIS...
            </>
          ) : (
            <>
              <Calendar className="w-4 h-4 mr-2" />
              Check Domain Age
            </>
          )}
        </Button>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Domain Age Hero */}
            <div className={`rounded-xl border p-5 ${getAgeCategory(result.ageInDays).bg} border-border`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Domain Age</p>
                  <p className={`text-3xl font-bold ${getAgeCategory(result.ageInDays).color}`}>
                    {result.ageFormatted || "Unknown"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {result.ageInDays ? `${result.ageInDays.toLocaleString()} days` : ""}
                  </p>
                </div>
                <div className={`px-4 py-2 rounded-full ${getAgeCategory(result.ageInDays).bg} border border-current/20`}>
                  <span className={`text-sm font-bold ${getAgeCategory(result.ageInDays).color}`}>
                    {getAgeCategory(result.ageInDays).label}
                  </span>
                </div>
              </div>
            </div>

            {/* Domain Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-muted/30 rounded-xl p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium text-muted-foreground uppercase">Registered</span>
                </div>
                <p className="text-sm font-semibold text-foreground">
                  {formatDate(result.registrationDate)}
                </p>
              </div>

              <div className="bg-muted/30 rounded-xl p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-warning" />
                  <span className="text-xs font-medium text-muted-foreground uppercase">Expires</span>
                </div>
                <p className="text-sm font-semibold text-foreground">
                  {formatDate(result.expirationDate)}
                </p>
                {result.expirationDate && (() => {
                  const daysLeft = getDaysUntilExpiry(result.expirationDate);
                  if (daysLeft !== null && daysLeft < 90) {
                    return (
                      <p className="text-xs text-warning mt-1">
                        ⚠️ Expires in {daysLeft} days
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>

              <div className="bg-muted/30 rounded-xl p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase">Last Updated</span>
                </div>
                <p className="text-sm font-semibold text-foreground">
                  {formatDate(result.updatedDate)}
                </p>
              </div>

              <div className="bg-muted/30 rounded-xl p-4 border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Building className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase">Registrar</span>
                </div>
                <p className="text-sm font-semibold text-foreground truncate">
                  {result.registrar || "Unknown"}
                </p>
              </div>
            </div>

            {/* Domain Status */}
            {result.status.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  Domain Status
                </h4>
                <div className="flex flex-wrap gap-2">
                  {result.status.slice(0, 6).map((status, index) => (
                    <span
                      key={index}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${
                        status.includes("active") || status.includes("ok")
                          ? "bg-success/10 text-success border-success/20"
                          : status.includes("lock") || status.includes("prohibited")
                          ? "bg-primary/10 text-primary border-primary/20"
                          : "bg-muted text-muted-foreground border-border"
                      }`}
                    >
                      {status.includes("active") || status.includes("ok") ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : null}
                      {status.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Email Reputation Note */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Why Domain Age Matters for Email</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {result.ageInDays && result.ageInDays < 180
                  ? "⚠️ New domains face deliverability challenges. Email providers are cautious of domains less than 6 months old. Consider a gradual warmup strategy."
                  : result.ageInDays && result.ageInDays < 365
                  ? "Your domain is building reputation. Continue consistent sending patterns and maintain good engagement metrics."
                  : "✓ Established domains typically have better email deliverability. Continue maintaining good sending practices."}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
